import type { DashboardTab } from "@/utils/DashboardLayouts";

/**
 * Local mirror of the user's server-side tab layout.
 *
 * The server is the source of truth. This cache is strictly a read-side
 * fallback for when the layout read fails, so the user sees their real
 * dashboard instead of defaults. Two rules keep it from corrupting the
 * server copy:
 *
 *  1. It is only ever written from a server-confirmed state — a successful
 *     read, or a save the server acknowledged. It never records unsaved edits.
 *  2. It is never an input to a write. Hydrating from cache leaves the store
 *     `tabsLoaded: false`, so the save subscription stays disarmed and a stale
 *     local layout can never be pushed over a newer one from another device.
 *
 * Keyed by Keycloak subject so a shared machine never shows one user the
 * other's dashboard.
 */

const CACHE_KEY = "newsquawk.tab-layout-cache";
const CACHE_VERSION = 1;

type CachedTabLayout = {
  version: number;
  userId: string;
  savedAt: string;
  tabs: DashboardTab[];
};

const isTabShaped = (value: unknown): value is DashboardTab =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as DashboardTab).id === "string" &&
  Array.isArray((value as DashboardTab).enabledWidgets);

/**
 * Returns the cached layout only if it belongs to this user and still matches
 * the expected shape. Anything else is treated as absent.
 */
export const readCachedTabs = (
  userId: string | undefined
): DashboardTab[] | null => {
  if (!userId) return null;

  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CachedTabLayout;
    if (parsed?.version !== CACHE_VERSION) return null;
    // A different user on this machine — never show them someone else's tabs.
    if (parsed.userId !== userId) return null;
    if (!Array.isArray(parsed.tabs) || parsed.tabs.length === 0) return null;
    if (!parsed.tabs.every(isTabShaped)) return null;

    return parsed.tabs;
  } catch {
    // Corrupt JSON, or storage unavailable (private mode, disabled cookies).
    return null;
  }
};

/**
 * Mirror a server-confirmed layout locally. Callers must not use this for
 * in-flight edits — see the module doc comment.
 */
export const writeCachedTabs = (
  userId: string | undefined,
  tabs: DashboardTab[]
): void => {
  if (!userId || !Array.isArray(tabs) || tabs.length === 0) return;

  try {
    const payload: CachedTabLayout = {
      version: CACHE_VERSION,
      userId,
      savedAt: new Date().toISOString(),
      tabs,
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Quota exceeded or storage unavailable — the cache is optional, so a
    // failure here must never interrupt the save path.
  }
};

export const clearCachedTabs = (): void => {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch {
    // Storage unavailable — nothing to do.
  }
};
